import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, Button, CircularProgress, Snackbar, Fab,
} from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { CheckCircle, Person, School, Subject, Check, Chat } from '@mui/icons-material';
import axios from 'axios';
// Assuming useAuth is available; if not, remove this import
// import { useAuth } from '../../services/AuthContext';
import { useAuth } from '../components/auth/AuthContext';

const API_BASE_URL = 'https://kidsm.vercel.app/api/schools';

const SchoolServices = () => {
  const { state } = useLocation();
  const { schoolId } = state || {};
  const navigate = useNavigate();
  // If using AuthContext, uncomment below and remove schoolId from state
  // const { user } = useAuth();

  const [transferRequests, setTransferRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parentNames, setParentNames] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const successAnim = useAnimation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const requestsResponse = await axios.get(`${API_BASE_URL}/${schoolId}/transfer-requests`);
        const requests = requestsResponse.data;
        setTransferRequests(requests);

        const uniqueParentIds = [...new Set(requests.map(req => req.parentId))];
        const names = {};

        await Promise.all(uniqueParentIds.map(async (parentId) => {
          try {
            const parentResponse = await axios.get(`${API_BASE_URL}/parents/${parentId}`);
            names[parentId] = parentResponse.data.parentName;
          } catch (error) {
            console.error(`Error fetching parent ${parentId}:`, error);
            names[parentId] = 'Parent';
          }
        }));

        setParentNames(names);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
        setSnackbar({ open: true, message: 'Failed to load transfer requests', severity: 'error' });
      }
    };

    if (schoolId) fetchData();
  }, [schoolId]);

  const showSuccessAnimation = async () => {
    await successAnim.start({ opacity: 1, scale: 1, transition: { duration: 0.3 } });
    await new Promise(resolve => setTimeout(resolve, 1000));
    await successAnim.start({ opacity: 0, scale: 0.8, transition: { duration: 0.3 } });
  };

  const handleStatusUpdate = async (requestId, status) => {
    try {
      await axios.patch(`${API_BASE_URL}/transfer-requests/${requestId}`, { status });
      await showSuccessAnimation();

      if (status === 'Processed') {
        setTransferRequests(prev => prev.filter(req => req._id !== requestId));
        setSnackbar({ open: true, message: 'Request marked as processed and removed.', severity: 'success' });
      } else {
        const response = await axios.get(`${API_BASE_URL}/${schoolId}/transfer-requests`);
        setTransferRequests(response.data);
        setSnackbar({ open: true, message: `Status updated to ${status}`, severity: 'success' });
      }
    } catch (error) {
      console.error('Error updating transfer request status:', error);
      setSnackbar({ open: true, message: 'Failed to update status.', severity: 'error' });
    }
  };

  const handleDirectProcess = async (requestId) => {
    try {
      await axios.patch(`${API_BASE_URL}/transfer-requests/${requestId}`, { status: 'Processed' });
      await showSuccessAnimation();
      setTransferRequests(prev => prev.filter(req => req._id !== requestId));
      setSnackbar({ open: true, message: 'Request processed successfully.', severity: 'success' });
    } catch (error) {
      console.error('Error processing request:', error);
      setSnackbar({ open: true, message: 'Failed to process request.', severity: 'error' });
    }
  };

  if (!schoolId) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ textAlign: 'center', color: '#333' }}>
          Error: No school ID provided
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh', position: 'relative' }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333', mb: 4 }}>
        Transfer Requests
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : transferRequests.length === 0 ? (
        <Typography sx={{ fontSize: '1rem', color: '#666', textAlign: 'center', my: 4 }}>
          No transfer requests found.
        </Typography>
      ) : (
        transferRequests.map((request) => (
          <Card key={request._id} sx={{ bgcolor: '#fff', borderRadius: 2, p: 2, mb: 2, boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#333', mb: 0.5 }}>
                Parent: {parentNames[request.parentId] || 'Parent'}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                Child: {request.childName}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                Admission: {request.admissionNumber}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                Subject: {request.subject}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 2 }}>
                Status:{' '}
                <span style={{
                  fontWeight: 'bold',
                  color: request.status === 'Received' ? '#FFA500' :
                        request.status === 'Processing' ? '#1E90FF' : '#32CD32',
                }}>
                  {request.status}
                </span>
              </Typography>

              {request.status !== 'Processed' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#666', mr: 2 }}>
                      Update Status:
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ bgcolor: '#6200ee', mr: 1, mb: 1, textTransform: 'none' }}
                      onClick={() => handleStatusUpdate(request._id, 'Received')}
                    >
                      Received
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ bgcolor: '#6200ee', mr: 1, mb: 1, textTransform: 'none' }}
                      onClick={() => handleStatusUpdate(request._id, 'Processing')}
                    >
                      Processing
                    </Button>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ bgcolor: '#4CAF50', borderRadius: 1, textTransform: 'none' }}
                    onClick={() => handleDirectProcess(request._id)}
                  >
                    Mark as Processed
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Fixed Action Buttons */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 160, right: 20, bgcolor: '#6200ee' }}
        onClick={() => navigate('/school-support-chat', { state: { schoolId } })}
      >
        <Chat />
      </Fab>
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 90, right: 20, bgcolor: '#6200ee' }}
        onClick={() => navigate('/fee-structure', { state: { schoolId } })}
      >
        <School />
      </Fab>
      <Fab
        color="secondary"
        sx={{ position: 'fixed', bottom: 20, right: 20, bgcolor: '#d9534f' }}
        onClick={() => navigate('/deleted-students', { state: { schoolId } })}
      >
        <Person />
      </Fab>

      {/* Success Animation Overlay */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={successAnim}
        style={{
          position: 'fixed',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: '#fff',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
        }}
      >
        <CheckCircle sx={{ fontSize: 80, color: '#4CAF50' }} />
        <Typography sx={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#333', mt: 1 }}>
          Updated Successfully!
        </Typography>
      </motion.div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        sx={{ bgcolor: snackbar.severity === 'success' ? '#4CAF50' : '#e74c3c' }}
      />
    </Container>
  );
};

export default SchoolServices;
